import React, { useState, useRef, useEffect } from 'react';
import { TFunction } from 'i18next';
import classes from 'classnames';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import styles from './BimloaFilter.module.scss';
import AppIcon from '../AppIcon/AppIcon';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import DropDown, { Option } from '../DropDown/DropDown';
import MultiSelectDropDown, {
  MultiSelectOption,
} from '@/components/school-management/MultiSelectDropDown/MultiSelectDropDown';
import { formatDateToStandard } from '@/utils/formatters';
import commonUtils from '@/utils/common';
import NumberRangeFilter, { NumberRangeFilterValue } from './NumberRangeFilter';
import AutocompleteInput, { AutocompleteItem, getDisplayText } from './AutocompleteInput';
import AutocompleteMultiSelectFilter from './AutocompleteMultiSelectFilter';
import { AnchorRefProps, createAnchorWrapper } from '@/components/school-management/Anchor/Anchor';
import type { CascadingFilterRequest } from '@/school-management/types/response/autocomplete';

export type { CascadingFilterRequest };

const ButtonAnchor = createAnchorWrapper('button');
const DivAnchor = createAnchorWrapper('div');

export type FilterType =
  | 'DATE'
  | 'NUMBER_RANGE'
  | 'AUTOCOMPLETE'
  | 'AUTOCOMPLETE_MULTI_SELECT'
  | 'TEXT'
  | 'MULTI_SELECT'
  | 'SINGLE_SELECT';

export const getFilterType = (field: FilterField): FilterType => {
  if (field.dateMode) return 'DATE';
  if (field.numberRangeMode) return 'NUMBER_RANGE';
  if (field.autocompleteType && field.multi) return 'AUTOCOMPLETE_MULTI_SELECT';
  if (field.autocompleteType) return 'AUTOCOMPLETE';
  if (field.textMode) return 'TEXT';
  if (field.multi) return 'MULTI_SELECT';
  return 'SINGLE_SELECT';
};

export const getEmptyValue = (field: FilterField) => {
  const filterType = getFilterType(field);

  switch (filterType) {
    case 'DATE':
      return { mode: 'none' } as DateFilterValue;
    case 'NUMBER_RANGE':
      return {} as NumberRangeFilterValue;
    case 'AUTOCOMPLETE':
      return null;
    case 'AUTOCOMPLETE_MULTI_SELECT':
      return [];
    case 'TEXT':
      return '';
    case 'MULTI_SELECT':
      return [];
    case 'SINGLE_SELECT':
      return 'All';
  }
};

export const isEmptyValue = (field: FilterField, value: any) => {
  const filterType = getFilterType(field);

  switch (filterType) {
    case 'DATE':
      return !(value && typeof value === 'object' && value.mode !== 'none');
    case 'NUMBER_RANGE':
      const rangeValue = value as NumberRangeFilterValue;
      return !(typeof rangeValue?.min === 'number' || typeof rangeValue?.max === 'number');
    case 'AUTOCOMPLETE':
      return !value;
    case 'AUTOCOMPLETE_MULTI_SELECT':
      return !(Array.isArray(value) && value.length > 0);
    case 'TEXT':
      return !(typeof value === 'string' && value.trim().length > 0);
    case 'MULTI_SELECT':
      return !(Array.isArray(value) && value.length > 0);
    case 'SINGLE_SELECT':
      return !value || value === 'All';
  }
};

const formatChip = (iso?: string) => {
  if (!iso) return '';
  const d = commonUtils.parseLocalDate(iso);
  return formatDateToStandard(d);
};

export const getFilterTagText = (field: FilterField, value: any, t: TFunction) => {
  const filterType = getFilterType(field);

  switch (filterType) {
    case 'DATE':
      if (value.mode === 'last7') return t('bimloaFilter.last7Days');
      if (value.mode === 'last30') return t('bimloaFilter.last30Days');
      if (value.mode === 'specific') return formatChip(value.date) || 'dd MMM yyyy';
      if (value.mode === 'range')
        return `${formatChip(value.from) || 'dd MMM yyyy'} - ${formatChip(value.to) || 'dd MMM yyyy'}`;
      return '';
    case 'NUMBER_RANGE':
      const textList = [];
      const minN = value.min;
      const maxN = value.max;
      if (typeof minN === 'number') {
        textList.push(`${t('bimloaFilter.min')}: ${minN}`);
      }
      if (typeof maxN === 'number') {
        textList.push(`${t('bimloaFilter.max')}: ${maxN}`);
      }
      return textList.join(' ');
    case 'AUTOCOMPLETE':
      return field.autocompleteType
        ? getDisplayText(value as AutocompleteItem, field.autocompleteType)
        : '';
    case 'AUTOCOMPLETE_MULTI_SELECT':
      const aciArr = (value || []) as AutocompleteItem[];
      const aciArrMax = 3;
      const aciHead = aciArr
        .slice(0, aciArrMax)
        .map(i => (field.autocompleteType ? getDisplayText(i, field.autocompleteType) : ''))
        .join(', ');
      const aciExtra = aciArr.length - aciArrMax;
      return aciExtra > 0 ? `${aciHead}, +${aciExtra}` : aciHead;
    case 'TEXT':
      return value as string;
    case 'MULTI_SELECT':
      const arr = value as string[];
      const max = 3;
      const head = arr.slice(0, max).join(', ');
      const extra = arr.length - max;
      return extra > 0 ? `${head}, +${extra}` : head;
    case 'SINGLE_SELECT':
      if (value === 'All' || !value) {
        return t('bimloaFilter.all') || 'All';
      }
      if (field.optionLabels && typeof value === 'string') {
        return field.optionLabels[value] ?? (value as string);
      }
      return value as string;
  }
};

// Types for dynamic filter fields
export type DateFilterMode = 'none' | 'last7' | 'last30' | 'specific' | 'range' | 'thisYear';
export type DateFilterValue = {
  mode: DateFilterMode;
  date?: string;
  from?: string;
  to?: string;
};

export type FilterValue =
  | string
  | string[]
  | DateFilterValue
  | NumberRangeFilterValue
  | AutocompleteItem
  | AutocompleteItem[]
  | null;

export type AutocompleteType = 'teachers' | 'students' | 'groups' | 'grades' | 'class_code';

export type FilterField = {
  key: string;
  label: string;
  options: string[];
  optionLabels?: Record<string, string>;
  multi?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  dateMode?: boolean;
  textMode?: boolean;
  textPlaceholder?: string;
  numberRangeMode?: {
    min?: number;
    max?: number;
    unit?: string;
    placeholder?: string;
  };
  autocompleteType?: AutocompleteType;
};

type BimloaFilterProps = {
  fields: FilterField[];
  values: Record<string, FilterValue>;
  onChange: (values: Record<string, FilterValue>) => void;
  isOpen?: boolean;
  style?: React.CSSProperties;
  anchorFilterOverlay?: boolean;
  anchorFilterOverlayPosition?: 'start' | 'end';
  anchorFilterOverlayVerticalOffset?: number;
  anchorFilterOverlayHorizontalOffset?: number;
  organizationId?: string;
  disabled?: boolean;
  cascadingFilters?: CascadingFilterRequest;
  anchorStayInView?: boolean;
};

const toDropDownOptions = (options: string[], optionLabels?: Record<string, string>): Option[] =>
  options
    .filter(opt => opt !== 'All')
    .map(opt => ({ label: optionLabels?.[opt] ?? opt, value: opt }));

const getCascadingFiltersForType = (
  type: AutocompleteType | undefined,
  cascadingFilters: CascadingFilterRequest | undefined
): CascadingFilterRequest | undefined => {
  if (!cascadingFilters || !type) return cascadingFilters;

  const { teacherIds, groupIds, studentIds, gradeIds, classCodes } = cascadingFilters;

  switch (type) {
    case 'teachers':
      return { groupIds, studentIds, gradeIds, classCodes };
    case 'groups':
      return { teacherIds, studentIds, gradeIds, classCodes };
    case 'students':
      return { teacherIds, groupIds, gradeIds, classCodes };
    case 'class_code':
      return { teacherIds, groupIds, studentIds, gradeIds };
    case 'grades':
      return { teacherIds, groupIds, studentIds, classCodes };
    default:
      return cascadingFilters;
  }
};

export const BimloaFilter: React.FC<BimloaFilterProps> = ({
  fields,
  values,
  onChange,
  isOpen = true,
  style,
  anchorFilterOverlay = false,
  anchorFilterOverlayPosition = 'start',
  anchorFilterOverlayVerticalOffset,
  anchorFilterOverlayHorizontalOffset,
  organizationId,
  disabled = false,
  cascadingFilters,
  anchorStayInView = true,
}) => {
  const { t } = useAdminTranslation();
  const [open, setOpen] = useState(!isOpen || false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openDateKey, setOpenDateKey] = useState<string | null>(null);
  const [openRangeFromKey, setOpenRangeFromKey] = useState<string | null>(null);
  const [openRangeToKey, setOpenRangeToKey] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const textInputDebounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [textInputValues, setTextInputValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      const handleOutsideClick = (event: any) => {
        if (containerRef.current && containerRef.current.contains(event.target)) {
          setOpen(true);
          return;
        }

        if (event.target.closest(`.${styles.filterDatePicker}`)) {
          setOpen(true);
          return;
        }

        setOpen(false);
      };

      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);

      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
        document.removeEventListener('touchstart', handleOutsideClick);
      };
    } else {
      setOpenDropdown(null);
      setOpenDateKey(null);
      setOpenRangeFromKey(null);
      setOpenRangeToKey(null);
    }
  }, [open]);

  useEffect(() => {
    const textFields = fields.filter(f => f.textMode);
    const newTextValues: Record<string, string> = {};
    textFields.forEach(f => {
      const filterValue = values[f.key];
      const value = typeof filterValue === 'string' ? filterValue : '';
      newTextValues[f.key] = value;
    });
    setTextInputValues(prev => {
      const hasChanges = textFields.some(f => {
        const filterValue = values[f.key];
        const currentValue = typeof filterValue === 'string' ? filterValue : '';
        return prev[f.key] !== currentValue;
      });
      return hasChanges ? { ...prev, ...newTextValues } : prev;
    });
  }, [values, fields]);

  useEffect(() => {
    return () => {
      Object.values(textInputDebounceTimers.current).forEach(timer => {
        clearTimeout(timer);
      });
      textInputDebounceTimers.current = {};
    };
  }, []);

  const formatISODate = (d: Date | null) => {
    if (!d) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const renderDateField = (f: FilterField) => {
    const v = values[f.key] as DateFilterValue;
    const modeOptions = [
      { label: t('bimloaFilter.last7Days'), value: 'last7' },
      { label: t('bimloaFilter.last30Days'), value: 'last30' },
      { label: t('bimloaFilter.specificDate'), value: 'specific' },
      { label: t('bimloaFilter.range'), value: 'range' },
    ];

    return (
      <div className={styles.dateModeContainer}>
        <DropDown
          options={modeOptions as unknown as Option[]}
          value={(modeOptions as any).find((o: any) => o.value === v?.mode)}
          onChange={opt => {
            const next: DateFilterValue = { mode: opt.value as DateFilterMode };
            onChange({ ...values, [f.key]: next });
          }}
          placeholder={(t('bimloaFilter.choose') as string) || ''}
          label=''
          Open={isEmptyValue(f, v)}
          autoScrollIntoView={true}
          autoClose={true}
          containerClassName={classes(
            styles.filterDropdownContainer,
            styles.filterRelativeDropdownContainer
          )}
          headerClassName={styles.filterDropdownHeader}
          listClassName={styles.filterDropdownList}
          arrowClassName={styles.filterDropdownArrow}
          itemClassName={styles.filterDropdownItem}
        />

        {v?.mode === 'specific' && (
          <>
            <label className={styles.label}>{t('bimloaFilter.chooseDate')}</label>
            <div className={styles.dateFieldWrapper}>
              <DivAnchor
                className={`${styles.filterDropdownHeader} ${styles.dateHeader}`}
                onClick={() => setOpenDateKey(f.key)}
                trigerType='none'
                autoClose={false}
                stayInView={true}
                open={openDateKey === f.key}
                tooltip={
                  <div className={styles.filterDatePicker}>
                    <DatePicker
                      selected={v.date ? commonUtils.parseLocalDate(v.date) : null}
                      onChange={(d: Date | null) => {
                        onChange({
                          ...values,
                          [f.key]: {
                            ...v,
                            date: formatISODate(d),
                          },
                        });
                        setOpenDateKey(null);
                      }}
                      open={true}
                      onClickOutside={() => setOpenDateKey(null)}
                      shouldCloseOnSelect
                      calendarStartDay={0}
                      fixedHeight
                      dateFormat='dd/MM/yyyy'
                      className={styles.hiddenDatePicker}
                      wrapperClassName={styles.datePickerWrapper}
                      popperPlacement='bottom'
                    />
                  </div>
                }
                renderTooltipPosition={anchorPosition => {
                  return {
                    top: anchorPosition.top + anchorPosition.height,
                    left: anchorPosition.left,
                    width: anchorPosition.width,
                    height: 1,
                  };
                }}
              >
                <span style={{ opacity: 0.7 }}>{v.date ? formatChip(v.date) : ''}</span>
                <button
                  type='button'
                  onClick={() => setOpenDateKey(f.key)}
                  className={styles.dateIconButton}
                  aria-label='Open calendar'
                >
                  <AppIcon icon='date' size={16} />
                </button>
              </DivAnchor>
            </div>
          </>
        )}

        {v?.mode === 'range' && (
          <>
            <label className={styles.label}>{t('bimloaFilter.chooseDateRange')}</label>
            <div className={styles.dateRangeGrid}>
              <DivAnchor
                className={styles.dateColumn}
                trigerType='none'
                autoClose={false}
                stayInView={true}
                open={openRangeFromKey === f.key}
                tooltip={
                  <div className={styles.filterDatePicker}>
                    <DatePicker
                      selected={v.from ? commonUtils.parseLocalDate(v.from) : null}
                      onChange={(d: Date | null) => {
                        const fromStr = formatISODate(d);
                        const next: DateFilterValue = { ...v, from: fromStr };
                        if (fromStr && v.to) {
                          const fromDate = commonUtils.parseLocalDate(fromStr);
                          const toDate = commonUtils.parseLocalDate(v.to);
                          fromDate.setHours(0, 0, 0, 0);
                          toDate.setHours(0, 0, 0, 0);
                          if (fromDate.getTime() > toDate.getTime()) {
                            next.to = fromStr;
                          }
                        }
                        onChange({
                          ...values,
                          [f.key]: next,
                        });
                        setOpenRangeFromKey(null);
                      }}
                      maxDate={v.to ? commonUtils.parseLocalDate(v.to) : undefined}
                      open={true}
                      onClickOutside={() => setOpenRangeFromKey(null)}
                      shouldCloseOnSelect
                      calendarStartDay={0}
                      fixedHeight
                      placeholderText='DD/MM/YYYY'
                      dateFormat='dd/MM/yyyy'
                      className={styles.hiddenDatePicker}
                      wrapperClassName={styles.datePickerWrapper}
                      popperPlacement='bottom-start'
                    />
                  </div>
                }
                renderTooltipPosition={anchorPosition => {
                  return {
                    top: anchorPosition.top + anchorPosition.height,
                    left: anchorPosition.left,
                    width: anchorPosition.width,
                    height: 1,
                  };
                }}
              >
                <div className={styles.dateFieldWrapper}>
                  <div
                    className={`${styles.filterDropdownHeader} ${styles.dateHeader}`}
                    onClick={() => setOpenRangeFromKey(f.key)}
                  >
                    <span style={{ opacity: 0.7 }}>{v.from ? formatChip(v.from) : ''}</span>
                    <button
                      type='button'
                      onClick={() => setOpenRangeFromKey(f.key)}
                      className={styles.dateIconButton}
                      aria-label='Open calendar'
                    >
                      <AppIcon icon='date' size={16} />
                    </button>
                  </div>
                </div>
              </DivAnchor>
              <DivAnchor
                className={styles.dateColumn}
                trigerType='none'
                autoClose={false}
                stayInView={true}
                open={openRangeToKey === f.key}
                tooltip={
                  <div className={styles.filterDatePicker}>
                    <DatePicker
                      selected={v.to ? commonUtils.parseLocalDate(v.to) : null}
                      onChange={(d: Date | null) => {
                        onChange({
                          ...values,
                          [f.key]: {
                            ...v,
                            to: formatISODate(d),
                          },
                        });
                        setOpenRangeToKey(null);
                      }}
                      minDate={v.from ? commonUtils.parseLocalDate(v.from) : undefined}
                      open={true}
                      onClickOutside={() => setOpenRangeToKey(null)}
                      shouldCloseOnSelect
                      calendarStartDay={0}
                      fixedHeight
                      placeholderText='DD/MM/YYYY'
                      dateFormat='dd/MM/yyyy'
                      className={styles.hiddenDatePicker}
                      wrapperClassName={styles.datePickerWrapper}
                      popperPlacement='bottom-end'
                    />
                  </div>
                }
                renderTooltipPosition={anchorPosition => {
                  return {
                    top: anchorPosition.top + anchorPosition.height,
                    left: anchorPosition.left,
                    width: anchorPosition.width,
                    height: 1,
                  };
                }}
              >
                <div className={styles.dateFieldWrapper}>
                  <div
                    className={`${styles.filterDropdownHeader} ${styles.dateHeader}`}
                    onClick={() => setOpenRangeToKey(f.key)}
                  >
                    <span style={{ opacity: 0.7 }}>{v.to ? formatChip(v.to) : ''}</span>
                    <button
                      type='button'
                      onClick={() => setOpenRangeToKey(f.key)}
                      className={styles.dateIconButton}
                      aria-label='Open calendar'
                    >
                      <AppIcon icon='date' size={16} />
                    </button>
                  </div>
                </div>
              </DivAnchor>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderDateFieldComp = (f: FilterField) => {
    const v = values[f.key];

    if (openDropdown === f.key || !isEmptyValue(f, v)) {
      return renderDateField(f);
    }

    const text = (() => {
      const dv = (v as DateFilterValue) || { mode: 'none' };
      if (dv.mode === 'last7') return t('bimloaFilter.last7Days');
      if (dv.mode === 'last30') return t('bimloaFilter.last30Days');
      if (dv.mode === 'thisYear') return t('bimloaFilter.thisYear');
      if (dv.mode === 'specific') return dv.date || t('bimloaFilter.specificDate');
      if (dv.mode === 'range') return `${dv.from || 'YYYY-MM-DD'} - ${dv.to || 'YYYY-MM-DD'}`;
      return t('bimloaFilter.all');
    })();

    return (
      <button className={styles.customSelect} onClick={() => setOpenDropdown(f.key)} type='button'>
        <span>{text}</span>
        <span className={styles.selectArrow}>
          <AppIcon icon='arrowDown' width={20} height={20} />
        </span>
      </button>
    );
  };

  const renderNumberRangeField = (f: FilterField) => {
    const value =
      values[f.key] &&
      (typeof (values[f.key] as any).min === 'number' ||
        typeof (values[f.key] as any).max === 'number')
        ? {
            min: (values[f.key] as any).min,
            max: (values[f.key] as any).max,
          }
        : ({} as NumberRangeFilterValue);

    return (
      <NumberRangeFilter
        min={f.numberRangeMode?.min}
        max={f.numberRangeMode?.max}
        value={value}
        unit={f.numberRangeMode?.unit}
        placeholder={f.numberRangeMode?.placeholder}
        onChange={newValue => {
          onChange({
            ...values,
            [f.key]:
              typeof newValue.min === 'number' || typeof newValue.max === 'number' ? newValue : {},
          });
        }}
      />
    );
  };

  const renderTextField = (f: FilterField) => {
    return (
      <input
        type='text'
        className={styles.textInput}
        placeholder={f.searchPlaceholder || f.label}
        value={
          textInputValues[f.key] !== undefined
            ? textInputValues[f.key]
            : (() => {
                const filterValue = values[f.key];
                return typeof filterValue === 'string' ? filterValue : '';
              })()
        }
        onChange={e => {
          const newValue = e.target.value;
          setTextInputValues(prev => ({ ...prev, [f.key]: newValue }));

          if (textInputDebounceTimers.current[f.key]) {
            clearTimeout(textInputDebounceTimers.current[f.key]);
          }

          textInputDebounceTimers.current[f.key] = setTimeout(() => {
            onChange({ ...values, [f.key]: newValue });
            delete textInputDebounceTimers.current[f.key];
          }, 500);
        }}
        onBlur={e => {
          const newValue = e.target.value;
          if (textInputDebounceTimers.current[f.key]) {
            clearTimeout(textInputDebounceTimers.current[f.key]);
            delete textInputDebounceTimers.current[f.key];
          }
          onChange({ ...values, [f.key]: newValue });
          setTextInputValues(prev => ({ ...prev, [f.key]: newValue }));
        }}
      />
    );
  };

  const renderAutocompleteField = (f: FilterField) => {
    const value = values[f.key] ? (values[f.key] as AutocompleteItem) : undefined;

    return organizationId && f.autocompleteType ? (
      <AutocompleteInput
        value={value}
        onChange={newValue => {
          onChange({ ...values, [f.key]: newValue });
        }}
        placeholder={f.searchPlaceholder || f.label}
        className={styles.textInput}
        organizationId={organizationId}
        autocompleteType={f.autocompleteType}
        cascadingFilters={getCascadingFiltersForType(f.autocompleteType, cascadingFilters)}
      />
    ) : (
      renderTextField(f)
    );
  };

  const renderAutocompleteMultiSelectField = (f: FilterField) => {
    const value = values[f.key] ? (values[f.key] as AutocompleteItem[]) : [];

    return (
      <AutocompleteMultiSelectFilter
        value={value}
        onChange={(opts: AutocompleteItem[]) => {
          onChange({ ...values, [f.key]: opts });
        }}
        isOpen={openDropdown === f.key}
        onOpen={() => {
          setOpenDropdown(f.key);
        }}
        autocompleteType={f.autocompleteType}
        label={f.label}
        searchPlaceholder={f.searchPlaceholder}
        organizationId={organizationId}
        cascadingFilters={getCascadingFiltersForType(f.autocompleteType, cascadingFilters)}
      />
    );
  };

  const renderMultiSelectField = (f: FilterField) => {
    if (openDropdown === f.key) {
      return (
        <div className={styles.filterMultiSelect}>
          <MultiSelectDropDown
            Open
            variant='filter'
            placeholder={f.label}
            searchPlaceholder={f.searchPlaceholder || t('bimloaFilter.all') || ''}
            options={toDropDownOptions(f.options).map(
              (o): MultiSelectOption => ({
                label: o.label,
                value: o.value,
                data: {},
              })
            )}
            value={
              Array.isArray(values[f.key])
                ? (values[f.key] as string[]).map(v => ({ label: v, value: v }))
                : []
            }
            onChange={(opts: MultiSelectOption[]) => {
              onChange({ ...values, [f.key]: opts.map(o => o.value) });
            }}
            containerClassName={styles.filterDropdownContainer}
            headerClassName={styles.filterDropdownHeader}
            listClassName={styles.filterDropdownList}
            arrowClassName={styles.filterDropdownArrow}
            itemClassName={styles.filterDropdownItem}
            autoScrollIntoView={true}
          />
        </div>
      );
    }

    const text = (() => {
      const v = values[f.key];
      const arr = Array.isArray(v) ? (v as string[]) : [];
      if (arr.length === 0) return t('bimloaFilter.all');
      return `${arr.length} ${t('multiSelectDropDown.selected', 'selected')}`;
    })();

    return (
      <button className={styles.customSelect} onClick={() => setOpenDropdown(f.key)} type='button'>
        <span>{text}</span>
        <span className={styles.selectArrow}>
          <AppIcon icon='arrowDown' width={20} height={20} />
        </span>
      </button>
    );
  };

  const renderSingleSelectField = (f: FilterField) => {
    const opts = toDropDownOptions(f.options, f.optionLabels);
    if (openDropdown === f.key) {
      return (
        <DropDown
          options={opts}
          value={opts.find(opt => opt.value === (values[f.key] || 'All'))}
          onChange={opt => {
            onChange({ ...values, [f.key]: opt.value });
            setOpenDropdown(null);
          }}
          placeholder={t('bimloaFilter.all') || ''}
          label=''
          Open={openDropdown === f.key}
          onBlur={() => setOpenDropdown(null)}
          containerClassName={classes(
            styles.filterDropdownContainer,
            styles.filterRelativeDropdownContainer
          )}
          headerClassName={styles.filterDropdownHeader}
          listClassName={styles.filterDropdownList}
          arrowClassName={styles.filterDropdownArrow}
          itemClassName={styles.filterDropdownItem}
          autoScrollIntoView={true}
        />
      );
    }

    const text = (() => {
      const v = values[f.key] as string | undefined;
      if (!v || v === 'All') return t('bimloaFilter.all');
      return f.optionLabels?.[v] ?? v;
    })();

    return (
      <button className={styles.customSelect} onClick={() => setOpenDropdown(f.key)} type='button'>
        <span>{text}</span>
        <span className={styles.selectArrow}>
          <AppIcon icon='arrowDown' width={20} height={20} />
        </span>
      </button>
    );
  };

  const getFilterComp = (f: FilterField) => {
    const filterType = getFilterType(f);

    switch (filterType) {
      case 'DATE':
        return renderDateFieldComp(f);
      case 'NUMBER_RANGE':
        return renderNumberRangeField(f);
      case 'AUTOCOMPLETE':
        return renderAutocompleteField(f);
      case 'AUTOCOMPLETE_MULTI_SELECT':
        return renderAutocompleteMultiSelectField(f);
      case 'TEXT':
        return renderTextField(f);
      case 'MULTI_SELECT':
        return renderMultiSelectField(f);
      case 'SINGLE_SELECT':
        return renderSingleSelectField(f);
    }
  };

  const handleResetAll = () => {
    const resetValues = Object.keys(values).reduce((acc, key) => {
      const field = fields.find(f => f.key === key);

      if (field) {
        const resetValue = getEmptyValue(field);
        return {
          ...acc,
          [key]: resetValue,
        };
      }

      return acc;
    }, {});
    onChange(resetValues);
  };

  const overlay = (
    <div
      ref={containerRef}
      className={classes(styles.overlay, {
        [styles.overlayTooltip]: anchorFilterOverlay,
        [styles.overflowHidden]: openDateKey || openRangeFromKey || openRangeToKey,
      })}
      style={style}
    >
      <div className={styles.filterHeader}>
        <h3 className={styles.title}>{t('bimloaFilter.filter')}</h3>
        {isOpen && (
          <button
            className={styles.close}
            onClick={() => setOpen(false)}
            aria-label={t('bimloaFilter.close') || ''}
          >
            <AppIcon icon='x' width={20} height={20} />
          </button>
        )}
      </div>

      {fields.map(f => (
        <div key={f.key} className={styles.filterRow}>
          <div className={styles.filterRowHeader}>
            <label className={styles.label}>{f.label}</label>
            <button
              className={styles.resetBtn}
              onClick={() => {
                const next = getEmptyValue(f);
                onChange({ ...values, [f.key]: next });
              }}
              disabled={(() => {
                const v = values[f.key] as any;
                return isEmptyValue(f, v);
              })()}
            >
              {t('bimloaFilter.reset')}
            </button>
          </div>
          {getFilterComp(f)}
        </div>
      ))}

      <button className={styles.resetAllBtn} onClick={handleResetAll}>
        {t('bimloaFilter.resetAll')}
      </button>
    </div>
  );

  const anyActive = fields.some(f => {
    return f && values[f.key] ? !isEmptyValue(f, values[f.key]) : false;
  });

  if (anchorFilterOverlay) {
    return (
      <>
        {isOpen && (
          <ButtonAnchor
            className={`${styles.filterByButton} ${anyActive ? styles.filterByButtonActive : ''} ${disabled ? styles.filterByButtonDisabled : ''}`}
            onClick={() => !disabled && setOpen(true)}
            trigerType='none'
            autoClose={false}
            open={open}
            stayInView={anchorStayInView}
            tooltip={overlay}
            disabled={disabled}
            renderTooltipPosition={anchorPosition => {
              let isLtr = true;
              const root = document.getElementById('root');
              if (root) {
                isLtr = window.getComputedStyle(root).direction === 'ltr';
              }

              const isHasVertical = typeof anchorFilterOverlayVerticalOffset === 'number';
              const isHasHorizontal = typeof anchorFilterOverlayHorizontalOffset === 'number';

              if (anchorFilterOverlayPosition === 'start' && isLtr) {
                return {
                  top: isHasVertical
                    ? anchorFilterOverlayVerticalOffset
                    : anchorPosition.top + anchorPosition.height + 10,
                  left: isHasHorizontal ? anchorFilterOverlayHorizontalOffset : anchorPosition.left,
                };
              }

              if (anchorFilterOverlayPosition === 'start' && !isLtr) {
                return {
                  top: isHasVertical
                    ? anchorFilterOverlayVerticalOffset
                    : anchorPosition.top + anchorPosition.height + 10,
                  right: isHasHorizontal
                    ? anchorFilterOverlayHorizontalOffset
                    : anchorPosition.right,
                };
              }

              if (anchorFilterOverlayPosition === 'end' && isLtr) {
                return {
                  top: isHasVertical
                    ? anchorFilterOverlayVerticalOffset
                    : anchorPosition.top + anchorPosition.height + 10,
                  right: isHasHorizontal
                    ? anchorFilterOverlayHorizontalOffset
                    : anchorPosition.right,
                };
              }

              return {
                top: isHasVertical
                  ? anchorFilterOverlayVerticalOffset
                  : anchorPosition.top + anchorPosition.height + 10,
                left: isHasHorizontal ? anchorFilterOverlayHorizontalOffset : anchorPosition.left,
              };
            }}
          >
            <AppIcon icon='filter' width={16} height={16} />
            <span className={styles.filterByButtonText}>{t('bimloaFilter.filterBy')}</span>
          </ButtonAnchor>
        )}
      </>
    );
  }

  return (
    <>
      {isOpen && (
        <button
          className={`${styles.filterByButton} ${anyActive ? styles.filterByButtonActive : ''} ${disabled ? styles.filterByButtonDisabled : ''}`}
          onClick={() => !disabled && setOpen(true)}
          disabled={disabled}
        >
          <AppIcon icon='filter' width={16} height={16} />
          <span className={styles.filterByButtonText}>{t('bimloaFilter.filterBy')}</span>
        </button>
      )}
      {open && overlay}
    </>
  );
};
